import * as styles from "./JobEditorDialog.css";

import { useAtomValue } from "jotai";
import type { JSX, RefObject } from "react";

import { Avatar } from "@frontend/components/atoms/Avatar";
import { Button } from "@frontend/components/atoms/Button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@frontend/components/atoms/Dialog";
import { Icon } from "@frontend/components/atoms/Icon";
import {
  ScrollArea,
  ScrollAreaContent,
  ScrollAreaScrollbar,
  ScrollAreaThumb,
  ScrollAreaViewport,
} from "@frontend/components/atoms/ScrollArea";
import { useDiscardGuard } from "@frontend/hooks/useDiscardGuard";
import { DiscardChangesDialog } from "@frontend/shared/discard-changes-dialog";
import { Trans, useLingui } from "@lingui/react/macro";

import { JobEditorBody } from "./components/JobEditorBody";
import { type UseJobEditorOptions, useJobEditor } from "./hooks/useJobEditor";
import { jobEditorAtom, jobForm } from "./stores/editor";

const DIALOG_WIDTH = 960;

export type JobEditorDialogProps = UseJobEditorOptions & {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  originRef?: RefObject<DOMRect | null>;
};

export function JobEditorDialog({
  open,
  onOpenChange,
  projectId,
  jobId = null,
  onSaved,
  onCancel,
  originRef,
}: JobEditorDialogProps): JSX.Element {
  const { t } = useLingui();

  const project = useAtomValue(jobEditorAtom).project;
  const dirty = useAtomValue(jobForm.state.isDirty);

  const { loading, saving, error, conflict, save, cancel } = useJobEditor({
    projectId,
    jobId,
    onSaved: (savedId) => {
      onOpenChange(false);
      onSaved?.(savedId);
    },
    onCancel: () => {
      onOpenChange(false);
      onCancel?.();
    },
  });

  const guard = useDiscardGuard({ dirty, onClose: cancel });

  return (
    <Dialog open={open} onOpenChange={guard.onOpenChange} originRef={originRef}>
      <DialogContent size={DIALOG_WIDTH} className={styles.popup}>
        <DialogHeader>
          <DialogTitle className={styles.title}>
            <Avatar
              className={styles.avatar}
              size="md"
              url={project?.clientAvatarUrl ?? undefined}
              fallback={<Icon name="Building2" size="sm" />}
            />

            <span className={styles.names}>
              <span className={styles.name}>
                {null === jobId ? t`New work post` : t`Work post`}
              </span>

              {project ? (
                <span className={styles.subtitle}>
                  {[project.clientLabel, project.label]
                    .filter(Boolean)
                    .join(" · ")}
                </span>
              ) : null}
            </span>
          </DialogTitle>

          <DialogClose />
        </DialogHeader>

        <ScrollArea className={styles.scrollArea}>
          <ScrollAreaViewport fade="end">
            <ScrollAreaContent
              className={styles.scroller}
              style={{ minWidth: 0 }}
            >
              <JobEditorBody
                loading={loading}
                error={error}
                conflict={conflict}
                stickyTabs
              />
            </ScrollAreaContent>
          </ScrollAreaViewport>

          <ScrollAreaScrollbar expandOnHover>
            <ScrollAreaThumb />
          </ScrollAreaScrollbar>
        </ScrollArea>

        <DialogFooter>
          <Button variant="neutral" onClick={guard.requestClose}>
            <Trans>Cancel</Trans>
          </Button>

          <Button
            variant="primary"
            loading={saving}
            disabled={loading}
            onClick={() => void save()}
          >
            <Trans>Save</Trans>
          </Button>
        </DialogFooter>

        <DiscardChangesDialog
          open={guard.confirming}
          onKeep={guard.keep}
          onDiscard={guard.discard}
        />
      </DialogContent>
    </Dialog>
  );
}
